import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, FilterIcon } from 'lucide-react';
import Breadcrumb from './breadcrumb';
import Table from './table';
import { LogsService } from '../services/logs.service';
import LogsFilterOverlay from './LogsFilterOverlay';
import { JobsiteLogsSkeleton } from './Skeleton';
import LogDetailsOverlay from './LogDetailsOverlay';

interface Log {
    id: string;
    actionType: string;
    timeStamp: string;
    userId: string;
    username?: string;
    entityType: string;
    newValue: Record<string, any>;
    previousValue: Record<string, any>;
    description: string;
}

// Add TableColumn interface
interface TableColumn {
    label: string;
    accessor: string;
    render?: (value: any, record?: any) => React.ReactNode;
}

// Update type definitions to match LogsFilterOverlay
type ActionType = 'CREATE' | 'UPDATE' | 'DELETE';
type EntityType = 'Job Details' | 'Job Address' | 'Jobsite Delete' | 'Comments' | 'Job Status' | 'Jobsite Visit Details' | 'Jobsite Visit Trades' | 'Jobsite Visit Files' | 'Jobsite Visit Comments';

interface LogFilters {
    startDate: string;
    endDate: string;
    actionType: ActionType[];
    entityType: EntityType[];
    search: string;
}

const JobsiteLogs: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const jobsiteId = location.state?.jobsiteId || id;
    const userId = location.state?.userId;
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const pageSize = 10;

    // Update active filters state with proper typing
    const [activeFilters, setActiveFilters] = useState<LogFilters>({
        startDate: '',
        endDate: '',
        actionType: [],
        entityType: [],
        search: ''
    });

    // Add state for filter overlay
    const [showFilterOverlay, setShowFilterOverlay] = useState(false);

    // Add a reference for tracking if filters were manually applied
    const [filtersApplied, setFiltersApplied] = useState(false);

    // Load initial data when the component mounts
    useEffect(() => {
        getLogs(1);
    }, [jobsiteId, userId]);

    // Sample data - replace with actual API call
    const [logs, setLogs] = useState<Log[]>([
        {
            id: '1',
            actionType: 'Sample description of action',
            timeStamp: 'Jan-22-2025 | 22:51',
            userId: 'Boyd Stevens',
            entityType: 'Job Status',
            newValue: { status: 'In Progress' },
            previousValue: {},
            description: 'Updated status'
        },
        {
            id: '2',
            actionType: 'Sample description of action',
            timeStamp: 'Jan-22-2025 | 22:51',
            userId: 'Boyd Stevens',
            entityType: 'Job Status',
            newValue: { status: 'Completed' },
            previousValue: {},
            description: 'Updated status'
        }
    ]);

    const getBadgeColor = (actionType: string): string => {
        const type = actionType.toLowerCase();
        if (type.includes('create') || type.includes('add')) {
            return 'bg-green-100 text-green-800';
        } else if (type.includes('delete') || type.includes('remove')) {
            return 'bg-red-100 text-red-800';
        } else if (type.includes('update') || type.includes('edit')) {
            return 'bg-blue-100 text-blue-800';
        } else if (type.includes('submit')) {
            return 'bg-purple-100 text-purple-800';
        } else {
            return 'bg-gray-100 text-gray-800';
        }
    };

    // Add state for selected log and show details
    const [selectedLog, setSelectedLog] = useState<Log | null>(null);
    const [showLogDetails, setShowLogDetails] = useState(false);

    // Update the columns definition to handle row clicks
    const columns: TableColumn[] = [
        { 
            label: 'Description', 
            accessor: 'description',
            render: (value: string, record:Log) => (
                <div 
                    className="cursor-pointer hover:text-[#A42823]"
                    onClick={(e) => {
                        e.stopPropagation();
                        const log = logs.find(log => log.id === record.id);
                        if (log) {
                            setSelectedLog(log);
                            setShowLogDetails(true);
                        }
                    }}
                    data-testid={`log-description-${value.replace(/\s+/g, '-').toLowerCase()}`}
                >
                    {value}
                </div>
            )
        },
        { 
            label: 'Action', 
            accessor: 'actionType',
            render: (value: string) => (
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getBadgeColor(value)}`}>
                    {value}
                </span>
            )
        },
        { label: 'Entity Type', accessor: 'entityType' },
        { label: 'Timestamp', accessor: 'timeStamp' },
    ];

    // Add debounce function
    const debounce = (func: Function, wait: number) => {
        let timeout: NodeJS.Timeout;
        return (...args: any[]) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), wait);
        };
    };

    // Add debounced search function
    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setActiveFilters(prev => ({
                ...prev,
                search: value
            }));
            setFiltersApplied(true);
            getLogs(1, value);
        }, 500),
        []
    );

    // Update handleSearch to use debounced search
    const handleSearch = (value: string) => {
        setSearchTerm(value);
        debouncedSearch(value);
    };

    // Add function to compare objects and find differences
    const findObjectDifferences = (prev: Record<string, any>, next: Record<string, any>, path: string = ''): string[] => {
        const differences: string[] = [];
        
        // Helper function to get the value description
        const getValueDescription = (value: any): string => {
            if (value === null) return 'null';
            if (value === undefined) return 'undefined';
            if (typeof value === 'object' && value.name) return value.name;
            if (Array.isArray(value)) return `array with ${value.length} items`;
            return String(value);
        };

        // Compare all keys in both objects
        const allKeys = new Set([...Object.keys(prev), ...Object.keys(next)]);

        allKeys.forEach(key => {
            const currentPath = path ? `${path}.${key}` : key;
            const prevValue = prev[key];
            const nextValue = next[key];

            // If key exists in both objects
            if (prev.hasOwnProperty(key) && next.hasOwnProperty(key)) {
                if (typeof prevValue === 'object' && typeof nextValue === 'object' && prevValue && nextValue) {
                    // Recursively compare nested objects
                    const nestedDiffs = findObjectDifferences(prevValue, nextValue, currentPath);
                    differences.push(...nestedDiffs);
                } else if (JSON.stringify(prevValue) !== JSON.stringify(nextValue)) {
                    differences.push(
                        `Changed ${currentPath}: from "${getValueDescription(prevValue)}" to "${getValueDescription(nextValue)}"`
                    );
                }
            }
            // If key only exists in previous value
            else if (prev.hasOwnProperty(key)) {
                differences.push(`Removed ${currentPath}: "${getValueDescription(prevValue)}"`);
            }
            // If key only exists in next value
            else {
                differences.push(`Added ${currentPath}: "${getValueDescription(nextValue)}"`);
            }
        });

        return differences;
    };

    // Count active filters
    const getActiveFiltersCount = () => {
        return (
            activeFilters.actionType.length +
            activeFilters.entityType.length +
            (activeFilters.startDate ? 1 : 0) +
            (activeFilters.endDate ? 1 : 0)
        );
    };

    // Update getLogs to include difference logging only for UPDATE and CREATE actions
    const getLogs = useCallback(async (page: number = 1, search: string = searchTerm) => {
        try {
            setLoading(true);
            
            // Include search parameter in the API call
            const response = await LogsService.getJobsiteLogs({
                jobId: jobsiteId,
                page: page,
                limit: pageSize,
                startDate: activeFilters.startDate,
                endDate: activeFilters.endDate,
                actionType: activeFilters.actionType,
                entityType: activeFilters.entityType,
                search: search.trim() // Add search parameter
            });
            
            // Remove client-side filtering since search is now handled by the API
            const filteredData = response.data;
            
            if (filteredData) {
                // Map logs with formatted data
                const formattedLogs = filteredData.map(log => {
                    // Format the log data based on entity type and action type
                    let formattedLog = {
                        id: log.id,
                        actionType: log.actionType,
                        entityType: log.entityType,
                        timeStamp: new Date(log.timeStamp).toLocaleString(),
                        userId: log.userId,
                        username: log.userName, // Use userName directly from the response
                        newValue: log.newValue,
                        previousValue: log.previousValue
                    };

                    // Format description based on entity type and action type
                    let description = '';
                    switch (log.entityType) {
                        case 'Job Details':
                            description = log.actionType === 'CREATE' ? 'Created jobsite' : 'Updated jobsite';
                            break;
                        case 'Job Address':
                            description = `${log.actionType === 'CREATE' ? 'Added' : 'Updated'} address`;
                            break;
                        case 'Job Status':
                            description = `${log.actionType === 'CREATE' ? 'Created' : 'Updated'} status`;
                            break;
                        case 'Comments':
                            description = `${log.actionType === 'CREATE' ? 'Added' : 'Updated'} comments`;
                            break;
                        case'Jobsite Delete' :
                             description = `${log.entityType}`;
                            break;
                        // Jobsite Visit entity types
                        case 'Jobsite Visit Details':
                            description = log.actionType === 'CREATE' ? 'Created visit' : 'Updated visit';
                            break;
                        case 'Jobsite Visit Trades':
                            description = `${log.actionType === 'CREATE' ? 'Added' : 'Updated'} visit trades`;
                            break;
                        case 'Jobsite Visit Files':
                            if (log.actionType === 'DELETE') {
                                description = 'Deleted visit files';
                            } else {
                                description = `${log.actionType === 'CREATE' ? 'Added' : 'Updated'} visit files`;
                            }
                            break;
                        case 'Jobsite Visit Comments':
                            description = `${log.actionType === 'CREATE' ? 'Added' : 'Updated'} visit comment`;
                            break;
                        default:
                            description = `${log.actionType} ${log.entityType}`;
                    }

                    return {
                        ...formattedLog,
                        description
                    };
                });
                
                setLogs(formattedLogs);
                setTotalItems(response.metadata.total); // Use API's total count
            }
        } catch (error) {
            console.error('Failed to fetch logs:', error);
        } finally {
            setLoading(false);
        }
    }, [jobsiteId, pageSize, searchTerm, activeFilters]);

    // Keep searchTerm and activeFilters.search in sync
    useEffect(() => {
        if (activeFilters.search !== searchTerm && !filtersApplied) {
            setSearchTerm(activeFilters.search);
        }
    }, [activeFilters.search]);

    // Update useEffect to only trigger getLogs when filtersApplied is true
    useEffect(() => {
        if (filtersApplied) {
            getLogs(1);
            setFiltersApplied(false);
        }
    }, [activeFilters, filtersApplied]);

    // Modify pagination useEffect
    useEffect(() => {
        if (!filtersApplied) {
            getLogs(currentPage, searchTerm);
        }
    }, [currentPage]);


    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Update the filter handler with proper typing
    const handleApplyFilters = (filters: LogFilters) => {
        setActiveFilters(filters);
        setShowFilterOverlay(false);
        setFiltersApplied(true); // Set the flag to trigger the useEffect
    };


    return (
        <div className="bg-gray-50 p-3 sm:p-4 md:p-6">
            <div className="mb-4 sm:mb-6">
                <div className="flex items-center">
                    <ArrowLeft 
                        className="text-gray-600 mr-2 w-5 h-5 cursor-pointer" 
                        onClick={() => navigate(-1)}
                        id="logs-back-button"
                    />
                    <Breadcrumb title="Jobsite Logs" />
                </div>
            </div>

            <div className="bg-white rounded-[12px] shadow overflow-hidden p-3 sm:p-4 mb-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
                            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                                <form className="relative flex-grow sm:flex-grow-0">
                                    <input
                                        type="text"
                                        placeholder="Search by Action or Entity Type"
                                         className="w-full sm:w-80 pl-10 pr-9 py-2 border border-border rounded-[8px] text-[16px] focus:outline-none"
                                        onChange={(e) => handleSearch(e.target.value)}
                                        value={searchTerm}
                                        id="logs-search-input"
                                    />
                                    <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    {searchTerm && (
                                        <button
                                            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                                            onClick={() => handleSearch('')}
                                            id="clear-search-button"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </form>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto justify-end">
                                <button
                                    onClick={() => setShowFilterOverlay(true)}
                                    className={`relative btn-secondary flex items-center justify-center text-sm sm:text-base w-full sm:w-auto ${
                                        getActiveFiltersCount() > 0 ? 'bg-[#A42823] text-white hover:bg-[#8a2320]' : ''
                                    }`}
                                    id="logs-filter-button"
                                >
                                    Filter
                                    <FilterIcon className="w-4 h-4 ml-1" />
                                    {getActiveFiltersCount() > 0 && (
                                        <span className="absolute -top-2 -right-2 bg-[#E6D69B] text-[#000000] text-[10px] rounded-full w-5 h-5 flex items-center justify-center">
                                            {getActiveFiltersCount()}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
            {loading ? (
                <JobsiteLogsSkeleton />
            ) : (
                <div className="">
                   

                    <div className="bg-white rounded-[12px] shadow p-3 sm:p-4">
                        <div className="relative min-h-[300px]">
                            {logs.length === 0 ? (
                                <div className="text-center py-6 sm:py-8" id="logs-empty-state">
                                    <div className="text-gray-500 mb-2 sm:mb-3 text-sm sm:text-base">No logs found</div>
                                    <div className="text-xs sm:text-sm text-gray-400">
                                        Try adjusting your search criteria
                                    </div>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <Table
                                        testId="logs-table"
                                        columns={columns}
                                        data={logs}
                                        totalItems={totalItems}
                                        currentPage={currentPage}
                                        pageSize={pageSize}
                                        onPageChange={handlePageChange}
                                        searchable={true}
                                        onSearch={handleSearch}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <LogsFilterOverlay
                        isOpen={showFilterOverlay}
                        onClose={() => setShowFilterOverlay(false)}
                        onApply={handleApplyFilters}
                        activeFilters={activeFilters}
                    />

                    <LogDetailsOverlay
                        isOpen={showLogDetails}
                        onClose={() => {
                            setShowLogDetails(false);
                            setSelectedLog(null);
                        }}
                        log={selectedLog}
                    />
                </div>
            )}
        </div>
    );
};

export default JobsiteLogs;
